import React from 'react';
import cn from 'classnames';
import { CircleAlert, CircleCheck, File as FileIcon } from 'lucide-react';
import SectionTitle from '../Shared/Tailwind/SectionTitle';
import SampleDocumentModal from './SampleDocumentModal';
import { getSamplePageByPath, getSamplePageTitleByPath, SAMPLE_DOCUMENT_FILE_NAME, SAMPLE_PAGE_PATH } from './samplePageRegistry';
import { getFileDisplayName } from './fileValidation';
import { PaymentRequestTypeValue, SCHOLARSHIP } from './paymentRequestTypes';
import UploadDropZone from './UploadDropZone';
import { requiredDocumentsByType } from './documentRequirements';
import InputField from '../Shared/Tailwind/InputField';

export interface UploadedDocuments {
  [documentId: string]: File[];
}

export type ExistingUploadedDocument = {
  id: number;
  documentId: string;
  originalFileName: string;
  contentType: string;
  fileSizeBytes: number;
};

export type ExistingUploadedDocumentsByDocumentId = Record<string, ExistingUploadedDocument[]>;

type PendingUploadedFile = {
  id: string;
  file: File;
};

type DisplayDocument = {
  key: string;
  documentId: string;
  fileName: string;
  fileSize: number;
  isExisting: boolean;
  fileIndex: number;
  pendingId?: string;
  isPending?: boolean;
};

const styles = {
  sectionWrap: 'self-stretch pt-5 flex flex-col justify-start items-start',
  stack: 'self-stretch flex flex-col justify-start items-start',
  uploadHeading: 'justify-start text-blue-950 text-lg font-bold leading-7',
  documentsListWrap: 'self-stretch overflow-hidden rounded-md border border-gray-300 divide-y divide-gray-300',
  documentBlock: 'self-stretch p-4 flex flex-col justify-start items-start',
  documentBlockMissing: 'bg-white shadow-[inset_0_0_0_0.25px_grey]',
  documentBlockUploaded: 'bg-[#E8F5E9] shadow-[inset_0_0_0_0.25px_grey]',
  documentHeader: 'self-stretch inline-flex justify-start items-center gap-2',
  documentTitle: 'left-0 top-0 absolute justify-start text-blue-950 text-sm font-semibold leading-5',
  documentDescriptionWrap: 'self-stretch pt-2 flex flex-col justify-start items-start',
  documentDescription: 'justify-start text-neutral-500 text-sm font-normal leading-5',
  sampleWrap: 'w-20 h-5 relative',
  sampleText: 'left-[-2px] top-[-1px] absolute text-center justify-start text-sky-800 text-sm font-medium underline leading-5 whitespace-nowrap',
  uploadRequiredDocumentDiv: 'self-stretch pt-4',
  displayRequiredDocumentsDiv: 'self-stretch pt-4 flex flex-col gap-3',
  uploadedFileBlock: 'self-stretch p-3 bg-green-50 border border-green-300 rounded-lg flex flex-col gap-1',
  uploadedFileName: 'text-green-800 text-sm font-semibold leading-5 flex items-center justify-between gap-3',
  uploadedFileMetaRight: 'flex items-center gap-2',
  documentTypeSelect: 'min-w-[220px] rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm text-neutral-700',
  removeButton: 'text-red-600 text-sm font-medium cursor-pointer hover:underline ml-2',
  fileSize: 'self-start text-green-700 text-xs leading-3',
  uploadCount: 'text-neutral-600 text-xs font-normal leading-4 mt-1',
};

interface UploadRequiredDocumentsProps {
  awardType?: PaymentRequestTypeValue;
  onUploadedFilesChange?: (files: UploadedDocuments) => void;
  onExistingUploadedDocumentsChange?: (documents: ExistingUploadedDocumentsByDocumentId) => void;
  onDocumentValidationChange?: (hasError: boolean) => void;
  initialUploadedFiles?: UploadedDocuments;
  initialUploadedDocuments?: ExistingUploadedDocumentsByDocumentId;
  readOnly?: boolean;
}

const UploadRequiredDocuments = ({
  awardType = SCHOLARSHIP,
  onUploadedFilesChange,
  onExistingUploadedDocumentsChange,
  onDocumentValidationChange,
  initialUploadedFiles,
  initialUploadedDocuments,
  readOnly = false,
}: UploadRequiredDocumentsProps) => {
  const [activeSampleFileName, setActiveSampleFileName] = React.useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedDocuments>(initialUploadedFiles || {});
  const [pendingUploadedFiles, setPendingUploadedFiles] = React.useState<PendingUploadedFile[]>([]);
  const [existingUploadedDocuments, setExistingUploadedDocuments] = React.useState<ExistingUploadedDocumentsByDocumentId>(
    initialUploadedDocuments || {}
  );
  const requiredDocuments = requiredDocumentsByType[awardType];
  const [selectedDocumentId, setSelectedDocumentId] = React.useState('');
  const activeInstructions = React.useMemo(() => {
    if (!activeSampleFileName) {
      return undefined;
    }

    return requiredDocuments.find((document) => document.sampleFileName === activeSampleFileName)?.instructions;
  }, [activeSampleFileName, requiredDocuments]);
  const activeContactInfo = React.useMemo(() => {
    if (!activeSampleFileName) {
      return undefined;
    }

    return requiredDocuments.find((document) => document.sampleFileName === activeSampleFileName)?.contactInfo;
  }, [activeSampleFileName, requiredDocuments]);
  const activeSampleTitle = React.useMemo(
    () => getSamplePageTitleByPath(SAMPLE_PAGE_PATH, activeSampleFileName ?? SAMPLE_DOCUMENT_FILE_NAME),
    [activeSampleFileName]
  );
  const ActiveSamplePage = getSamplePageByPath(SAMPLE_PAGE_PATH, activeSampleFileName ?? SAMPLE_DOCUMENT_FILE_NAME);

  React.useEffect(() => {
    setUploadedFiles(initialUploadedFiles || {});
  }, [initialUploadedFiles]);

  React.useEffect(() => {
    setExistingUploadedDocuments(initialUploadedDocuments || {});
  }, [initialUploadedDocuments]);

  React.useEffect(() => {
    if (!requiredDocuments.some((document) => document.documentId === selectedDocumentId) && selectedDocumentId !== '') {
      setSelectedDocumentId('');
    }
  }, [requiredDocuments, selectedDocumentId]);

  React.useEffect(() => {
    onDocumentValidationChange?.(pendingUploadedFiles.length > 0);
  }, [onDocumentValidationChange, pendingUploadedFiles.length]);

  const allowedDocumentIds = React.useMemo(() => new Set(requiredDocuments.map((document) => document.documentId)), [requiredDocuments]);

  const displayDocuments = React.useMemo<DisplayDocument[]>(() => {
    const existingDocs = Object.entries(existingUploadedDocuments)
      .filter(([documentId]) => allowedDocumentIds.has(documentId))
      .flatMap(([documentId, files]) =>
        files.map((file, index) => ({
          key: `existing-${file.id}`,
          documentId,
          fileName: file.originalFileName,
          fileSize: file.fileSizeBytes,
          isExisting: true,
          fileIndex: index,
        }))
      );

    const newDocs = Object.entries(uploadedFiles)
      .filter(([documentId]) => allowedDocumentIds.has(documentId))
      .flatMap(([documentId, files]) =>
        files.map((file, index) => ({
          key: `new-${documentId}-${file.name}-${index}`,
          documentId,
          fileName: getFileDisplayName(file),
          fileSize: file.size,
          isExisting: false,
          fileIndex: index,
        }))
      );

    const pendingDocs = pendingUploadedFiles.map((pendingFile) => ({
      key: `pending-${pendingFile.id}`,
      documentId: '',
      fileName: getFileDisplayName(pendingFile.file),
      fileSize: pendingFile.file.size,
      isExisting: false,
      fileIndex: 0,
      pendingId: pendingFile.id,
      isPending: true,
    }));

    return [...existingDocs, ...newDocs, ...pendingDocs];
  }, [allowedDocumentIds, existingUploadedDocuments, pendingUploadedFiles, uploadedFiles]);

  const handleFilesAdd = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    const pendingFiles = files.map((file) => ({
      id: `${Date.now()}-${Math.random()}-${file.name}-${file.size}`,
      file,
    }));

    setPendingUploadedFiles((previous) => [...previous, ...pendingFiles]);
  };

  const handleRemoveUploaded = (documentId: string, indexToRemove: number) => {
    const updatedFiles = {
      ...uploadedFiles,
      [documentId]: (uploadedFiles[documentId] || []).filter((_, index) => index !== indexToRemove),
    };
    if ((updatedFiles[documentId] || []).length === 0) {
      delete updatedFiles[documentId];
    }
    setUploadedFiles(updatedFiles);
    onUploadedFilesChange?.(updatedFiles);
  };

  const handleRemovePending = (pendingId: string) => {
    const nextPendingFiles = pendingUploadedFiles.filter((pending) => pending.id !== pendingId);
    setPendingUploadedFiles(nextPendingFiles);
  };

  const handleAssignPendingDocumentType = (pendingId: string, documentId: string) => {
    const pendingFile = pendingUploadedFiles.find((item) => item.id === pendingId);
    if (!pendingFile || !documentId) {
      return;
    }

    const updatedFiles = {
      ...uploadedFiles,
      [documentId]: [...(uploadedFiles[documentId] || []), pendingFile.file],
    };

    setUploadedFiles(updatedFiles);
    setPendingUploadedFiles((previous) => previous.filter((item) => item.id !== pendingId));
    onUploadedFilesChange?.(updatedFiles);
  };

  const handleReassignUploadedDocumentType = (documentId: string, indexToRemove: number, nextDocumentId: string) => {
    if (documentId === nextDocumentId || !nextDocumentId) {
      return;
    }

    const filesForDocument = [...(uploadedFiles[documentId] || [])];
    const [selectedFile] = filesForDocument.splice(indexToRemove, 1);
    if (!selectedFile) {
      return;
    }

    const updatedFiles = { ...uploadedFiles };
    if (filesForDocument.length === 0) {
      delete updatedFiles[documentId];
    } else {
      updatedFiles[documentId] = filesForDocument;
    }
    updatedFiles[nextDocumentId] = [...(updatedFiles[nextDocumentId] || []), selectedFile];

    setUploadedFiles(updatedFiles);
    onUploadedFilesChange?.(updatedFiles);
  };

  const handleRemoveExisting = (documentId: string, indexToRemove: number) => {
    const updatedDocuments = {
      ...existingUploadedDocuments,
      [documentId]: (existingUploadedDocuments[documentId] || []).filter((_, index) => index !== indexToRemove),
    };
    if ((updatedDocuments[documentId] || []).length === 0) {
      delete updatedDocuments[documentId];
    }
    setExistingUploadedDocuments(updatedDocuments);
    onExistingUploadedDocumentsChange?.(updatedDocuments);
  };

  return (
    <div className={cn(styles.sectionWrap)}>
      <div className={cn(styles.stack)}>
        <SectionTitle className={styles.uploadHeading} as='h3'>
          Upload Required Documents
        </SectionTitle>

        <div id='list-required-documents' className={cn(styles.documentsListWrap)}>
          <div className={cn(styles.stack)}>
            {requiredDocuments.map((document) => {
              const hasUpload =
                (uploadedFiles[document.documentId]?.length ?? 0) > 0 || (existingUploadedDocuments[document.documentId]?.length ?? 0) > 0;
              return (
                <div
                  key={document.title}
                  className={cn(styles.documentBlock, hasUpload ? styles.documentBlockUploaded : styles.documentBlockMissing)}
                >
                  <div className={cn(styles.stack)}>
                    <div className={cn(styles.documentHeader)}>
                      <div className='flex flex-col items-start w-6 h-6 pr-2'>
                        {hasUpload ? <CircleCheck className='text-evergreen ' /> : <CircleAlert />}
                      </div>
                      <div className={cn(document.titleWrapClassName)}>
                        <div className={cn(styles.documentTitle)}>{document.title}</div>
                      </div>
                      <div className={cn(styles.sampleWrap)}>
                        <button
                          type='button'
                          className={cn(styles.sampleText, 'bg-transparent border-0 p-0 cursor-pointer')}
                          onClick={() => setActiveSampleFileName(document.sampleFileName)}
                        >
                          View Sample
                        </button>
                      </div>
                    </div>
                    <div className={cn(styles.documentDescriptionWrap)}>
                      <div className={cn(styles.stack)}>
                        <div className={cn(styles.documentDescription)}>{document.description}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div id='display-required-documents-div' className={cn(styles.displayRequiredDocumentsDiv)}>
          <div className={cn(styles.uploadCount)}>{displayDocuments.length} file(s) uploaded</div>
          {displayDocuments.map((displayDocument) => {
            const selectValue = displayDocument.isPending ? '' : displayDocument.documentId;
            const selectLabel = displayDocument.isPending ? 'Select document type' : 'Uploaded document type';
            const hasTypeError = displayDocument.isPending;

            return (
              <div key={displayDocument.key} className={cn(styles.uploadedFileBlock, hasTypeError && 'border-red-300 bg-red-50')}>
                <div className={cn(styles.uploadedFileName)}>
                  <span className='inline-flex gap-1'>
                    <FileIcon />
                    {displayDocument.fileName}
                  </span>
                  <div className={cn(styles.uploadedFileMetaRight)}>
                    <InputField
                      name={`document-type-${displayDocument.key}`}
                      label={selectLabel}
                      type='select'
                      value={selectValue}
                      required={hasTypeError}
                      options={[
                        { label: 'Select Document Type', value: '', disabled: true },
                        ...requiredDocuments.map((document) => ({ label: document.title, value: document.documentId })),
                      ]}
                      onChange={(nextDocumentId) => {
                        if (displayDocument.isPending && displayDocument.pendingId) {
                          handleAssignPendingDocumentType(displayDocument.pendingId, nextDocumentId);
                          return;
                        }

                        if (!displayDocument.isPending && displayDocument.documentId) {
                          handleReassignUploadedDocumentType(displayDocument.documentId, displayDocument.fileIndex, nextDocumentId);
                        }
                      }}
                      inputClassName={cn(styles.documentTypeSelect, hasTypeError && 'border-red-300 text-red-700')}
                      labelClassName='sr-only'
                      wrapperClassName='w-full'
                      dataTestId={`document-type-select-${displayDocument.key}`}
                    />
                    {!readOnly && (
                      <button
                        type='button'
                        className={cn(styles.removeButton)}
                        onClick={() => {
                          if (displayDocument.isPending && displayDocument.pendingId) {
                            handleRemovePending(displayDocument.pendingId);
                            return;
                          }

                          if (displayDocument.isExisting) {
                            handleRemoveExisting(displayDocument.documentId, displayDocument.fileIndex);
                          } else {
                            handleRemoveUploaded(displayDocument.documentId, displayDocument.fileIndex);
                          }
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                {hasTypeError && <div className='text-red-600 text-xs mt-1'>Document type is required to complete this upload.</div>}
              </div>
            );
          })}
        </div>

        <div id='upload-required-document-div' className={cn(styles.uploadRequiredDocumentDiv)}>
          <UploadDropZone
            documentTypes={requiredDocuments.map((document) => ({
              documentId: document.documentId,
              title: document.title,
              maxFiles: document.maxFiles,
              currentCount: (uploadedFiles[document.documentId]?.length ?? 0) + (existingUploadedDocuments[document.documentId]?.length ?? 0),
            }))}
            onFilesAdd={handleFilesAdd}
            readOnly={readOnly}
          />
        </div>
      </div>

      <SampleDocumentModal
        isOpen={activeSampleFileName !== null}
        onClose={() => setActiveSampleFileName(null)}
        page={activeSampleFileName ? <ActiveSamplePage /> : null}
        title={activeSampleTitle}
        instructionalText={activeInstructions}
        contactInfo={activeContactInfo}
      />
    </div>
  );
};

export default UploadRequiredDocuments;
